import React, { useState } from 'react'
import { DEFAULT_IMAGE_POSITION, EMPTY_SLIDE, MAX_SLIDES } from './TabSlides'

// Stamps the blob as ours so a stray paste is rejected outright instead of half-applying.
const FORMAT = 'kokemuksia-widget'
const VERSION = 1

const text = value => (typeof value === 'string' ? value : '')

const color = (value, fallback) => (typeof value === 'string' && value ? value : fallback)

// Same rule as `parsePixels()` in api/src/routes/dashboard/widget.js, but kept as a string
// because that is what the size inputs hold.
const size = value => {
  const parsed = parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : ''
}

const sizeValue = value => {
  const parsed = size(value)
  return parsed ? parseInt(parsed, 10) : null
}

// Media is dropped in both directions: a file id only resolves in the environment the file
// was uploaded to, and a focal point only means anything for the image it was picked on. A
// pasted widget therefore starts out as if it never had media, and it gets picked again in
// the Sisältö tab.
function normalizeSlide(raw) {
  const slide = raw && typeof raw === 'object' ? raw : {}
  return {
    titleText: text(slide.titleText),
    contentText: text(slide.contentText),
    personName: text(slide.personName),
    personTitle: text(slide.personTitle),
    company: text(slide.company),
    ctaText: text(slide.ctaText),
    ctaUrl: text(slide.ctaUrl),
    fileId: '',
    imagePosition: { ...DEFAULT_IMAGE_POSITION },
  }
}

const monospace = { fontFamily: 'monospace', fontSize: '12px' }

export default function TabCopy({
  title, setTitle,
  description, setDescription,
  domainList, setDomain,
  layout, setLayout,
  textColor, setTextColor,
  accentColor, setAccentColor,
  backgroundColor, setBackgroundColor,
  maxWidth, setMaxWidth,
  titleFontSize, setTitleFontSize,
  contentFontSize, setContentFontSize,
  customCss, setCustomCss,
  slides, setSlides,
}) {
  const [input, setInput] = useState('')
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState('')
  const [imported, setImported] = useState(false)

  const exported = JSON.stringify(
    {
      format: FORMAT,
      version: VERSION,
      widget: {
        title,
        description,
        domains: domainList,
        layout,
        textColor,
        accentColor,
        backgroundColor,
        maxWidth: sizeValue(maxWidth),
        titleFontSize: sizeValue(titleFontSize),
        contentFontSize: sizeValue(contentFontSize),
        customCss,
        slides: slides.map(normalizeSlide),
      },
    },
    null,
    2
  )

  // Pasting replaces the whole editor state, so it is worth a second click once there is
  // something to lose. Nothing is persisted either way until the widget is saved.
  const hasContent = !!(
    title.trim() ||
    customCss.trim() ||
    slides.some(slide => text(slide.titleText).trim() || text(slide.contentText).trim())
  )

  const handleCopy = () => {
    navigator.clipboard.writeText(exported).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const resetImportState = () => {
    setConfirming(false)
    setImported(false)
    setError('')
  }

  const applyImport = () => {
    let parsed
    try {
      parsed = JSON.parse(input)
    } catch (_) {
      setError('Sisältö ei ole kelvollista JSONia.')
      setConfirming(false)
      return
    }

    const widget = parsed && parsed.widget
    if (!parsed || parsed.format !== FORMAT || parsed.version !== VERSION || !widget || typeof widget !== 'object') {
      setError('Tuntematon sisältö — odotettiin widgetin kopiointidataa.')
      setConfirming(false)
      return
    }

    setTitle(text(widget.title))
    setDescription(text(widget.description))
    setDomain(Array.isArray(widget.domains) ? widget.domains.join(', ') : text(widget.domains))
    setLayout(text(widget.layout))
    setTextColor(color(widget.textColor, '#111111'))
    setAccentColor(color(widget.accentColor, '#000000'))
    setBackgroundColor(color(widget.backgroundColor, '#ffffff'))
    setMaxWidth(size(widget.maxWidth))
    setTitleFontSize(size(widget.titleFontSize))
    setContentFontSize(size(widget.contentFontSize))
    setCustomCss(text(widget.customCss))

    // The editor always renders at least one slide.
    const rawSlides = Array.isArray(widget.slides) ? widget.slides.slice(0, MAX_SLIDES) : []
    setSlides(rawSlides.length > 0 ? rawSlides.map(normalizeSlide) : [{ ...EMPTY_SLIDE, imagePosition: { ...DEFAULT_IMAGE_POSITION } }])

    setConfirming(false)
    setError('')
    setImported(true)
  }

  const handleImport = () => {
    if (hasContent && !confirming) {
      setConfirming(true)
      setError('')
      setImported(false)
      return
    }
    applyImport()
  }

  return (
    <React.Fragment>
      <p className="form-hint">
        Kopioi widgetin asetukset ympäristöstä toiseen. Mediat eivät siirry, vaan ne valitaan uudelleen Sisältö-välilehdellä.
      </p>

      <div className="widget-section-heading">Kopioi</div>
      <div className="widget-form-field">
        <textarea readOnly value={exported} rows={12} style={monospace} onFocus={e => e.target.select()} />
      </div>
      <button className="widget-copy-btn" onClick={handleCopy}>
        {copied ? 'Kopioitu!' : 'Kopioi leikepöydälle'}
      </button>

      <div className="widget-section-heading" style={{ marginTop: '24px' }}>Liitä</div>
      {error && <div className="passcode-error">{error}</div>}
      {imported && <div className="widget-saved">Liitetty. Tarkista tiedot ja tallenna.</div>}
      <div className="widget-form-field">
        <textarea
          value={input}
          onChange={e => {
            setInput(e.target.value)
            resetImportState()
          }}
          rows={12}
          placeholder={`{\n  "format": "${FORMAT}",\n  ...\n}`}
          style={monospace}
        />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button className="widget-new-btn" onClick={handleImport} disabled={!input.trim()}>
          {confirming ? 'Vahvista liittäminen' : 'Liitä'}
        </button>
        {confirming && (
          <button className="widget-cancel-btn" onClick={() => setConfirming(false)}>
            Peruuta
          </button>
        )}
      </div>
    </React.Fragment>
  )
}
